import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@mantine/core';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getMeasurementDetailListQuery } from '../../../slices/measurementDetail/thunk';
import AddEditMeasurementDetailsModal from './Add_Edit/AddEditMeasurementDetails';

const MeasurementDetailsList = () => {
	const selectorProfile = createSelector(
		state => state.Profile,
		profile => ({
			establishmentActive: profile.establishmentActive,
		})
	);
	const { establishmentActive } = useSelector(selectorProfile);
	const selectedEstablishmentId = establishmentActive?.item?._id || null;

	const [dataMeasurementDetail, setDataMeasurementDetail] = useState({});
	const [modal, setModal] = useState(false);
	const [refetch, setRefetch] = useState(0);

	const toggleRefetch = () => setRefetch(prev => prev + 1);

	const handleToggleModal = () => setModal(prev => !prev);

	const handleClickMakeReading = data => {
		setDataMeasurementDetail(data);
		setModal(true);
	};
	// DD/MM/YYYY
	const formatDate = dateString => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '-';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	// Format numeric values with fixed decimals
	const formatNumber = (value, decimals = 2) => {
		if (value === null || value === undefined) return '-';
		return Number(value).toFixed(decimals);
	};

	// measurement_status.name → Badge color
	const getStatusColor = name => {
		if (!name) return 'gray';
		switch (name) {
			case 'Cumple':
				return 'green';
			case 'Bajo':
			case 'Medio':
				return 'yellow';
			case 'No cumple':
				return 'red';
			case 'En espera de resultados':
				return 'cyan';
			default:
				return 'gray';
		}
	};

	const getReportabilityStatusColor = name => {
		if (!name) return 'gray';
		switch (name) {
			case 'Programado':
				return 'blue';
			case 'Reportado':
				return 'green';
			case 'Fuera de Plazo':
				return 'yellow';
			case 'Atrasado':
				return 'red';
			case 'En espera de resultados':
				return 'cyan';
			case 'Pendiente':
				return 'cyan';
			default:
				return 'gray';
		}
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'measurementDetails',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const canMeasure = !!cellProps.row.original?.can_measure &&
					cellProps.row.original?.scheduled_activity?.activity_status?.name !== 'Reportado';
					return (
						<div className='d-flex justify-content-center'>
							<Link
								to='#'
								title={canMeasure ? 'Realizar Medición' : 'No disponible para medir'}
								onClick={e => {
									e.preventDefault();
									if (!canMeasure) return;
									handleClickMakeReading(cellProps.row.original);
								}}
								className={canMeasure ? '' : 'disabled'}
								style={{ pointerEvents: canMeasure ? 'auto' : 'none' }}
							>
								<i className={`ri-file-add-line align-bottom fs-16 ${canMeasure ? 'text-primary' : 'text-muted'}`}></i>
							</Link>
						</div>
					);
				},
			},
			{
				header: 'Estado',
				dataType: 'select',
				accessorKey: 'measurement_status.name',
				Cell: cellProps => {
					const name = cellProps.row.original.measurement_status?.name;
					return (
						<Badge color={getStatusColor(name)} tt='capitalize'>
							{name || '-'}
						</Badge>
					);
				},
			},
			{
				header: 'Estado de Reportabilidad',
				dataType: 'select',
				accessorKey: 'scheduled_activity.activity_status.name',
				Cell: cellProps => {
					const name = cellProps.row.original.scheduled_activity?.activity_status?.name;
					return (
						<Badge color={getReportabilityStatusColor(name)} tt='capitalize'>
							{name || '-'}
						</Badge>
					);
				},
			},
			{
				header: 'Código de Obra',
				dataType: 'string',
				accessorKey: 'work_order.work_order_code',
			},
			{
				header: 'Establecimiento',
				dataType: 'multi-select',
				accessorKey: 'work_order.water_right.establishment.name',
			},
			{
				header: 'Fecha límite',
				dataType: 'date',
				defaultSort: 'asc',
				accessorKey: 'display_deadline',
				Cell: cellProps => {
					return <span>{formatDate(cellProps.row.original.display_deadline)}</span>;
				},
			},
			{
				header: 'Fecha de Medición',
				dataType: 'date',
				accessorKey: 'measurement_date',
				Cell: cellProps => {
					return <span>{formatDate(cellProps.row.original.measurement_date)}</span>;
				},
			},
			{
				header: 'Hora Medición',
				dataType: 'string',
				accessorKey: 'measurement_time',
			},
			{
				header: 'Caudal (L/s)',
				dataType: 'float.2',
				accessorKey: 'flow_rate',
				Cell: cellProps => {
					return <span>{formatNumber(cellProps.row.original.flow_rate)}</span>;
				},
			},
			{
				header: 'Límite Caudal (L/s)',
				dataType: 'float.2',
				accessorKey: 'flow_rate_limit',
				Cell: cellProps => {
					return <span>{formatNumber(cellProps.row.original.flow_rate_limit)}</span>;
				},
			},
			{
				header: 'Totalizador (m³)',
				dataType: 'float.2',
				accessorKey: 'totalizer',
				Cell: cellProps => {
					return <span>{formatNumber(cellProps.row.original.totalizer)}</span>;
				},
			},
			{
				header: 'Nivel Freático (m)',
				dataType: 'float.2',
				accessorKey: 'water_table_level',
				Cell: cellProps => {
					return <span>{formatNumber(cellProps.row.original.water_table_level)}</span>;
				},
			},
		],
		[]
	);

	const baseFilters = useMemo(() => {
		const filters = {
			disable: { $eq: false },
		};

		if (selectedEstablishmentId && selectedEstablishmentId !== 'all') {
			filters['work_order.water_right.establishment._id'] = {
				$eq: selectedEstablishmentId,
			};
		}

		return filters;
	}, [selectedEstablishmentId]);

	return (
		<React.Fragment>
			<ODataGrid
				columns={columns}
				getDataFunc={getMeasurementDetailListQuery}
				refetchRemoteData={refetch}
				baseFilters={baseFilters}
				queryPopulate={[
					'measurement_status'
				]}
				remoteOperations={{ 
					pagination: true, 
					sorting: true, 
					filtering: true 
				}}
				getRowIdTable={row => row._id}
				nameFile='Detalle de Mediciones Globales'
				
			/>
			<AddEditMeasurementDetailsModal
				dataMeasurementDetail={dataMeasurementDetail}
				isEdit={false}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				toggleRefetch={toggleRefetch}
			/>
		</React.Fragment>
	);
};

export default MeasurementDetailsList;
